import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkle, Download, Trash, Image as ImageIcon, ArrowLeft, Spinner, MagicWand, TextAa, Copy, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

const FORMATS = [
  { value: "square", label: "SQUARE 1:1", hint: "Feed ads" },
  { value: "landscape", label: "LANDSCAPE", hint: "Link / desktop ads" },
  { value: "story", label: "STORY 9:16", hint: "Stories / Reels" },
];

function CopyLine({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={copy} className="w-full flex items-start gap-2 text-left bg-[#0a0a0a] border border-[#1f1f1f] hover:border-[#ccff00]/40 rounded-sm px-2.5 py-2 transition-colors group" data-testid={testId}>
      <span className="text-[11px] text-[#ccc] leading-relaxed flex-1 whitespace-pre-wrap">{text}</span>
      <Copy size={12} className={`shrink-0 mt-0.5 ${copied ? "text-[#4ade80]" : "text-[#555] group-hover:text-[#ccff00]"}`} />
    </button>
  );
}

function AdCopyPanel({ graphic, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/ad-graphics/${graphic.id}/copy`, {}, { timeout: 120000 });
      onUpdate(graphic.id, r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Copy generation failed — try again");
    }
    setLoading(false);
  }, [graphic.id, onUpdate]);

  useEffect(() => { if (!graphic.ad_copy) generate(); }, [graphic.ad_copy, generate]);

  if (loading) return (
    <div className="mt-3 pt-3 border-t border-[#262626]" data-testid={`ad-copy-loading-${graphic.id}`}>
      <p className="text-[10px] text-[#ccff00] font-bold uppercase tracking-wider animate-pulse">WRITING AD COPY...</p>
    </div>
  );
  const c = graphic.ad_copy;
  if (!c) return null;
  return (
    <div className="mt-3 pt-3 border-t border-[#262626]" data-testid={`ad-copy-panel-${graphic.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#888]">FACEBOOK AD COPY <span className="text-[#555] normal-case font-normal">— click any line to copy</span></p>
        <button onClick={generate} className="flex items-center gap-1 text-[9px] font-bold text-[#888] hover:text-[#ccff00] uppercase" data-testid={`ad-copy-regen-${graphic.id}`}>
          <ArrowsClockwise size={11} /> REGENERATE
        </button>
      </div>
      <p className="text-[9px] font-bold uppercase text-[#555] mb-1">HEADLINES</p>
      <div className="space-y-1 mb-2.5">
        {(c.headlines || []).map((h, i) => <CopyLine key={i} text={h} testId={`ad-copy-headline-${graphic.id}-${i}`} />)}
      </div>
      <p className="text-[9px] font-bold uppercase text-[#555] mb-1">PRIMARY TEXT</p>
      <div className="space-y-1 mb-2.5">
        {(c.primary_texts || []).map((t, i) => <CopyLine key={i} text={t} testId={`ad-copy-primary-${graphic.id}-${i}`} />)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[9px] font-bold uppercase text-[#555] mb-1">LINK DESCRIPTION</p>
          {c.description && <CopyLine text={c.description} testId={`ad-copy-desc-${graphic.id}`} />}
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase text-[#555] mb-1">CTA BUTTON</p>
          {c.cta && <CopyLine text={c.cta} testId={`ad-copy-cta-${graphic.id}`} />}
        </div>
      </div>
    </div>
  );
}

function AdImage({ id, name }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    axios.get(`${API}/admin/ad-graphics/${id}/image`, { responseType: "blob" })
      .then(r => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (failed) return <div className="w-full aspect-square flex items-center justify-center bg-[#131313]"><ImageIcon size={24} className="text-[#333]" /></div>;
  if (!src) return <div className="w-full aspect-square skeleton" />;
  return <img src={src} alt={name} className="w-full h-auto" loading="lazy" />;
}

export default function AdGraphicsStudio({ user }) {
  const [graphics, setGraphics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [format, setFormat] = useState("square");
  const [brandStyle, setBrandStyle] = useState(true);
  const [remixTarget, setRemixTarget] = useState(null);
  const [remixPrompt, setRemixPrompt] = useState("");
  const [remixing, setRemixing] = useState(false);
  const [copyOpen, setCopyOpen] = useState(null);

  const updateAdCopy = useCallback((id, adCopy) => {
    setGraphics(list => list.map(x => x.id === id ? { ...x, ad_copy: adCopy } : x));
  }, []);

  const fetchGraphics = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/ad-graphics`);
      setGraphics(r.data.graphics || []);
    } catch { toast.error("Failed to load ad graphics"); }
    setLoading(false);
  }, []);

  useEffect(() => { if (user?.is_admin) fetchGraphics(); }, [user, fetchGraphics]);

  const generate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) { toast.error("Describe the ad you want"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/ad-graphics/generate`, { prompt: prompt.trim(), name: name.trim(), format, brand_style: brandStyle }, { timeout: 180000 });
      setGraphics(g => [r.data, ...g]);
      setPrompt(""); setName("");
      toast.success("Ad graphic generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed — try again");
    }
    setGenerating(false);
  };

  const download = async (g) => {
    try {
      const r = await axios.get(`${API}/admin/ad-graphics/${g.id}/image`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(g.name || "ad-graphic").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.${g.content_type?.includes("png") ? "png" : "jpg"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const remix = async (g) => {
    if (!remixPrompt.trim()) { toast.error("Describe the change you want"); return; }
    setRemixing(true);
    try {
      const r = await axios.post(`${API}/admin/ad-graphics/${g.id}/remix`, { prompt: remixPrompt.trim() }, { timeout: 180000 });
      setGraphics(list => [r.data, ...list]);
      setRemixTarget(null); setRemixPrompt("");
      toast.success("Remix saved to library");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remix failed — try again");
    }
    setRemixing(false);
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/ad-graphics/${g.id}`);
      setGraphics(list => list.filter(x => x.id !== g.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (!user?.is_admin) {
    return <div className="max-w-xl mx-auto px-4 py-24 text-center"><p className="text-sm text-[#888]">Admin access required.</p></div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6">
      <div className="flex items-center gap-3 mb-6">
        <a href="/admin" className="text-[#888] hover:text-[#ccff00]" data-testid="ad-studio-back-link"><ArrowLeft size={18} weight="bold" /></a>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase">AD <span className="text-[#ccff00]">STUDIO</span></h1>
          <p className="text-[10px] text-[#666]">Facebook ad graphics library + AI generator</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator panel */}
        <form onSubmit={generate} className="lg:col-span-1 border border-[#262626] bg-[#131313] rounded-sm p-5 h-fit lg:sticky lg:top-20">
          <p className="text-xs font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2"><Sparkle size={14} weight="bold" className="text-[#ccff00]" /> GENERATE NEW AD</p>

          <label className="text-[10px] uppercase tracking-wider text-[#888] block mb-1.5">Name</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rookie Draft Angle" className="h-9 bg-[#0a0a0a] border-[#333] text-white text-xs rounded-sm mb-4" data-testid="ad-studio-name-input" />

          <label className="text-[10px] uppercase tracking-wider text-[#888] block mb-1.5">Format</label>
          <div className="grid grid-cols-3 gap-1.5 mb-4">
            {FORMATS.map(f => (
              <button key={f.value} type="button" onClick={() => setFormat(f.value)} data-testid={`ad-studio-format-${f.value}`}
                className={`p-2 rounded-sm border text-center transition-colors ${format === f.value ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" : "border-[#333] text-[#888] hover:border-[#555]"}`}>
                <span className="block text-[9px] font-bold">{f.label}</span>
                <span className="block text-[8px] text-[#666]">{f.hint}</span>
              </button>
            ))}
          </div>

          <label className="text-[10px] uppercase tracking-wider text-[#888] block mb-1.5">Describe the ad</label>
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={5} data-testid="ad-studio-prompt-input"
            placeholder={"e.g. Bold headline 'DOMINATE YOUR ROOKIE DRAFT' with a draft board mockup showing rookie pick cards and hit-rate percentages"}
            className="w-full bg-[#0a0a0a] border border-[#333] text-white text-xs rounded-sm p-3 placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none resize-none mb-3" />

          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input type="checkbox" checked={brandStyle} onChange={e => setBrandStyle(e.target.checked)} className="accent-[#ccff00]" data-testid="ad-studio-brand-checkbox" />
            <span className="text-[10px] text-[#888]">Apply DTM brand style (dark + volt lime)</span>
          </label>

          <Button type="submit" disabled={generating} className="w-full h-11 bg-[#ccff00] text-black font-bold text-xs rounded-sm hover:bg-[#b8e600] uppercase" data-testid="ad-studio-generate-btn">
            {generating ? <span className="flex items-center gap-2"><Spinner size={14} className="animate-spin" /> GENERATING (~30S)...</span> : "GENERATE AD GRAPHIC"}
          </Button>
          <p className="text-[9px] text-[#555] mt-2 text-center">Uses your Emergent key balance. Saved to your library automatically.</p>
        </form>

        {/* Gallery */}
        <div className="lg:col-span-2">
          <p className="text-xs font-bold text-white uppercase tracking-wider mb-4">LIBRARY <span className="text-[#666] font-normal">({graphics.length})</span></p>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{[1, 2, 3, 4].map(i => <div key={i} className="aspect-square skeleton rounded-sm" />)}</div>
          ) : graphics.length === 0 ? (
            <p className="text-xs text-[#666]">No graphics yet. Generate your first one.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ad-studio-gallery">
              {graphics.map(g => (
                <div key={g.id} className="border border-[#262626] bg-[#131313] rounded-sm overflow-hidden" data-testid={`ad-graphic-card-${g.id}`}>
                  <AdImage id={g.id} name={g.name} />
                  <div className="p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs font-bold text-white truncate">{g.name}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-sm border border-[#333] text-[#888]">{g.format}</span>
                        {g.source === "generated" && <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-[#ccff00]/10 text-[#ccff00]">AI</span>}
                        {g.source === "remixed" && <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-[#c084fc]/10 text-[#c084fc]">REMIX</span>}
                      </div>
                    </div>
                    <p className="text-[10px] text-[#666] line-clamp-2 mb-3">{g.prompt}</p>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => download(g)} className="h-8 px-3 flex-1 bg-transparent border border-[#333] text-[#ccc] hover:border-[#ccff00] hover:text-[#ccff00] rounded-sm text-[10px]" data-testid={`ad-graphic-download-${g.id}`}>
                        <Download size={12} className="mr-1" /> DOWNLOAD
                      </Button>
                      <Button onClick={() => { setRemixTarget(remixTarget?.id === g.id ? null : g); setRemixPrompt(""); }} className="h-8 px-3 bg-transparent border border-[#333] text-[#ccc] hover:border-[#c084fc] hover:text-[#c084fc] rounded-sm text-[10px]" data-testid={`ad-graphic-remix-${g.id}`}>
                        <MagicWand size={12} className="mr-1" /> REMIX
                      </Button>
                      <Button onClick={() => setCopyOpen(copyOpen === g.id ? null : g.id)} className="h-8 px-3 bg-transparent border border-[#333] text-[#ccc] hover:border-[#4ecdc4] hover:text-[#4ecdc4] rounded-sm text-[10px]" data-testid={`ad-graphic-copywriter-${g.id}`}>
                        <TextAa size={12} className="mr-1" /> COPY
                      </Button>
                      <Button onClick={() => remove(g)} className="h-8 px-3 bg-transparent border border-[#333] text-[#888] hover:border-[#ff6b6b] hover:text-[#ff6b6b] rounded-sm text-[10px]" data-testid={`ad-graphic-delete-${g.id}`}>
                        <Trash size={12} />
                      </Button>
                    </div>
                    {remixTarget?.id === g.id && (
                      <div className="mt-3 pt-3 border-t border-[#262626]" data-testid={`ad-graphic-remix-panel-${g.id}`}>
                        <textarea value={remixPrompt} onChange={e => setRemixPrompt(e.target.value)} rows={3} autoFocus
                          placeholder={"e.g. Change the headline to 'WIN YOUR LEAGUE THIS YEAR' / make the accent color gold / remove the phone mockup"}
                          className="w-full bg-[#0a0a0a] border border-[#333] text-white text-xs rounded-sm p-2.5 placeholder:text-[#555] focus:border-[#c084fc] focus:outline-none resize-none mb-2"
                          data-testid={`ad-graphic-remix-input-${g.id}`} />
                        <div className="flex items-center gap-2">
                          <Button onClick={() => remix(g)} disabled={remixing} className="h-8 px-3 flex-1 bg-[#c084fc] text-black font-bold rounded-sm text-[10px] hover:bg-[#b06ef0] uppercase" data-testid={`ad-graphic-remix-apply-${g.id}`}>
                            {remixing ? <span className="flex items-center gap-1.5"><Spinner size={12} className="animate-spin" /> REMIXING (~30S)...</span> : "APPLY REMIX"}
                          </Button>
                          <Button onClick={() => setRemixTarget(null)} disabled={remixing} className="h-8 px-3 bg-transparent border border-[#333] text-[#888] hover:text-white rounded-sm text-[10px]">CANCEL</Button>
                        </div>
                        <p className="text-[9px] text-[#555] mt-1.5">Saved as a new graphic — the original stays in your library.</p>
                      </div>
                    )}
                    {copyOpen === g.id && <AdCopyPanel graphic={g} onUpdate={updateAdCopy} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
